import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackupList from './BackupList';
import CreateBackupModal from './CreateBackupModal';
import RestoreBackupModal from './RestoreBackupModal';
import './BackupDatabase.css';

const BackupDatabase = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [backupType, setBackupType] = useState('full'); // 'full' or 'district'
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');

  useEffect(() => {
    loadBackups();
    loadDistricts();
  }, []);

  const loadBackups = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/backups/list');
      setBackups(response.data);
    } catch (error) {
      console.error('Failed to load backups:', error);
      alert('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const loadDistricts = async () => {
    try {
      const response = await axios.get('/api/districts');
      setDistricts(response.data);
    } catch (error) {
      console.error('Failed to load districts:', error);
    }
  };

  const handleCreateBackup = async (type, districtId = null) => {
    try {
      setLoading(true);
      let response;
      
      if (type === 'full') {
        response = await axios.post('/api/backups/create');
      } else {
        response = await axios.post(`/api/backups/create-district/${districtId}`);
      }

      if (response.data.success) {
        alert('Backup created successfully!');
        setShowCreateModal(false);
        loadBackups();
      }
    } catch (error) {
      console.error('Failed to create backup:', error);
      alert(`Failed to create backup: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (backup, confirm, targetDistrictId = null) => {
    try {
      setLoading(true);
      let response;

      if (backup.backup_type === 'full') {
        response = await axios.post('/api/backups/restore', {
          filename: backup.filename,
          confirm
        });
      } else {
        response = await axios.post('/api/backups/restore-district', {
          filename: backup.filename,
          districtId: targetDistrictId,
          confirm
        });
      }

      if (response.data.success) {
        alert('Backup restored successfully!');
        setShowRestoreModal(false);
        setSelectedBackup(null);
      }
    } catch (error) {
      console.error('Failed to restore backup:', error);
      alert(`Failed to restore backup: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      setLoading(true);
      // Use axios to download with auth token
      const response = await axios.get(`/api/backups/download/${filename}`, {
        responseType: 'blob', // Important: tell axios to expect binary data
      });

      // Create a blob URL from the response
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download backup:', error);
      alert(`Failed to download backup: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (backup) => {
    if (!window.confirm(`Are you sure you want to delete backup "${backup.filename}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/api/backups/${backup.filename}`);
      alert('Backup deleted successfully');
      loadBackups();
    } catch (error) {
      console.error('Failed to delete backup:', error);
      alert(`Failed to delete backup: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="backup-database-container">
      <div className="backup-header">
        <div>
          <h2>Database Backup & Restore</h2>
          <p className="backup-storage-info">
            Backups are stored in: <code>backend/backups/</code>
          </p>
        </div>
        <button
          onClick={() => navigate('/superadmin')}
          className="btn-secondary"
        >
          Back to Dashboard
        </button>
      </div>

      <div className="backup-tabs">
        <div className="backup-section">
          <h3>Create Backup</h3>
          <div className="backup-actions">
            <button
              onClick={() => {
                setBackupType('full');
                setShowCreateModal(true);
              }}
              className="btn-primary"
              disabled={loading}
            >
              Backup Entire Database
            </button>
            <button
              onClick={() => {
                setBackupType('district');
                setShowCreateModal(true);
              }}
              className="btn-primary"
              disabled={loading}
            >
              Backup District
            </button>
          </div>
        </div>

        <div className="backup-section">
          <h3>Restore Backup</h3>
          <p className="backup-warning">
            ⚠️ Warning: Restoring a backup will overwrite existing data. This action cannot be undone.
          </p>
        </div>
      </div>

      <div className="backup-list-section">
        <h3>Available Backups</h3>
        {loading && <p>Loading backups...</p>}
        {!loading && backups.length === 0 && (
          <p>No backups available. Create your first backup above.</p>
        )}
        {!loading && backups.length > 0 && (
          <BackupList
            backups={backups}
            onRestore={(backup) => {
              setSelectedBackup(backup);
              setShowRestoreModal(true);
            }}
            onDownload={handleDownload}
            onDelete={handleDelete}
            formatFileSize={formatFileSize}
            formatDate={formatDate}
          />
        )}
      </div>

      {showCreateModal && (
        <CreateBackupModal
          backupType={backupType}
          districts={districts}
          selectedDistrict={selectedDistrict}
          onDistrictChange={setSelectedDistrict}
          onCreate={handleCreateBackup}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedDistrict('');
          }}
        />
      )}

      {showRestoreModal && selectedBackup && (
        <RestoreBackupModal
          backup={selectedBackup}
          districts={districts}
          onRestore={handleRestore}
          onClose={() => {
            setShowRestoreModal(false);
            setSelectedBackup(null);
          }}
        />
      )}
    </div>
  );
};

export default BackupDatabase;
